<template>
	<div class="flex flex_column">
		<div class="flex flex_between">
			<label class="md-field-header" :class="{ 'md-error': invalidDateError || error}">
				{{ $t("common.dateOfBirth") + (required ? " *" : "")}}
			</label>
			<span v-if="invalidDateError" class="md-field-header validation-error">{{ $t('common.validation.messages.is_date')}}</span>
			<span v-else-if="error" class="md-field-header validation-error">{{ error }}</span>
		</div>

		<div class="flex flex_between">
			<md-field class="flex_1 margin_r" :class="[{ 'md-error': error && !form.yearPart}]" style="min-width: 75px">
				<label>{{ $t('common.year') }}</label>
				<md-input v-model="form.yearPart"
						  @input="updateDate"
						  type="number"
						  data-vv-name="yearPart"
						  v-validate="modelValidations.yearPart"
						  :required="required"
						  :disabled="disabled"
				></md-input>
			</md-field>
			<md-field class="flex_1 margin_r" :class="[{ 'md-error':  error && !form.monthPart}]" style="min-width: 150px">
				<label>{{ $t('common.month') }}</label>
				<TbSelect v-model="form.monthPart"
						  @input="updateDate"
						  :options="monthOptions"
						  options-value-field="index"
						  options-display-field="name"
						  :required="required"
						  :disabled="disabled"
				></TbSelect>
			</md-field>
			<md-field class="flex_1" :class="[{ 'md-error':  error && !form.dayPart}]" style="min-width: 75px">
				<label>{{ $t('common.day') }}</label>
				<md-input v-model="form.dayPart"
						  @input="updateDate"
						  type="number"
						  data-vv-name="dayPart"
						  v-validate="modelValidations.dayPart"
						  :required="required"
						  :disabled="disabled"
				></md-input>
			</md-field>
		</div>
	</div>
</template>

<script>
import {toMomentDateFormat} from "@/common/format";
import {validateForm, validateRequiredFields} from "@/validation/validation";

export default {
	props: {
		value: { type: String, default: null },
		valueFormat: { type: String, default: "dd.MM.yyyy" },
		required: { type: Boolean, default: false },
		disabled: { type: Boolean, default: false },
		error: { type: String, default: "" }
	},
	data() {
		let defaultForm = {
			dayPart: null,
			monthPart: null,
			yearPart: null,
		};

		return {
			defaultForm: defaultForm,
			form: { ...defaultForm },
			modelValidations: {
				dayPart: {
					required: this.required,
					min_value: 1,
					max_value: 31,
				},
				monthPart: {
					required: this.required,
					min_value: 0,
					max_value: 11,
				},
				yearPart: {
					required: this.required,
					min_value: 1900,
					max_value: 9999,
				}
			},
			monthOptions: moment.localeData().months().map((value, index) => {
				return {
					index: index,
					name: value
				}
			}),
			ignoreValueUpdates: false,
			invalidDateError: "",
		};
	},
	beforeMount() {
		this.updateForm();
	},
	methods: {
		updateForm() {
			// We have to make sure to not update the internal data when we just emitted "null" for example due to some parts missing.
			// And we do not want to clear all parts just because a single part for example was cleared by the user.
			if (this.ignoreValueUpdates) {
				return;
			}

			if (this.value) {
				let date = moment(this.value, toMomentDateFormat(this.valueFormat));
				this.form.dayPart = date.get("D");
				this.form.monthPart = date.get("M");
				this.form.yearPart = date.get("y");
			} else {
				this.form = { ...this.defaultForm };
			}
		},
		updateDate() {
			this.ignoreValueUpdates = true;
			this.invalidDateError = false;

			if (!this.isFormValid) {
				this.$emit("input", null);
				return;
			}

			validateForm(this, this.$validator)
				.then(() => {
					let date = moment({y: this.form.yearPart, M: this.form.monthPart, d: this.form.dayPart});

					if (date.isValid()) {
						this.value = date.format(toMomentDateFormat(this.valueFormat));
						this.$emit("input", this.value);
					} else {
						this.markDateAsInvalid();
					}
				})
				.catch(() => {
					this.markDateAsInvalid();
				})
				.finally(() => {
					this.ignoreValueUpdates = false;
				});
		},
		markDateAsInvalid() {
			this.invalidDateError = true;
			this.$emit("input", null);
		}
	},
	computed: {
		isFormValid() {
			return validateRequiredFields(this.form, this.modelValidations);
		}
	},
	watch: {
		value() {
			this.updateForm();
		}
	}
};
</script>

<style lang="scss" scoped>
.md-input {
	min-width: 100%;
}

.md-input[type=number]::-webkit-inner-spin-button {
	-webkit-appearance: none;
}
</style>